import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout';
import PracticeCloseButton from './PracticeCloseButton';
import { getLastOpenedUrl } from '../../../utils/lastOpened';

/**
 * Shared UI states for practice pages (Loading, Error, Submitting, NoQuestions)
 */

const pageWrapper =
  'quick-practice-page w-full flex flex-col items-center justify-center min-h-screen box-border p-6 md:p-4 md:px-6 max-md:p-0 max-md:min-h-[100dvh] max-md:items-stretch max-md:justify-start max-md:pb-0';
const loadingContainer =
  'flex flex-col items-center justify-center py-20 px-10 text-center max-md:py-10 max-md:px-4';
const spinner =
  'w-[60px] h-[60px] border-4 border-[rgba(18,118,109,0.1)] border-t-[#12766d] rounded-full animate-spin mb-6 max-md:w-[50px] max-md:h-[50px] max-md:mb-5';
const loadingText = 'text-lg font-semibold text-[var(--text-primary)] mb-2 m-0 max-md:text-base';
const loadingSubtext = 'text-sm font-normal text-[rgba(26,26,46,0.6)] m-0 max-md:text-[13px]';

export const PracticeLoadingState = () => (
  <Layout hideSidebar>
    <div className={pageWrapper}>
      <div className={loadingContainer}>
        <div className={spinner} aria-hidden />
        <p className={loadingText}>Loading practice questions...</p>
      </div>
    </div>
  </Layout>
);

export const PracticeSubmittingState = () => (
  <Layout hideSidebar>
    <div className={pageWrapper}>
      <div className={loadingContainer}>
        <div className={spinner} aria-hidden />
        <p className={loadingText}>Submitting your answers...</p>
        <p className={loadingSubtext}>Please wait while we grade your practice</p>
      </div>
    </div>
  </Layout>
);

const cardContainer =
  'w-full max-w-[1560px] md:max-w-[1061px] mx-auto p-6 md:p-4 md:px-5 bg-[var(--card-bg)] rounded-2xl shadow-[var(--shadow-sm)] flex flex-col relative max-md:max-w-none max-md:rounded-none max-md:shadow-none max-md:p-4 max-md:px-3 max-md:pb-[140px] max-md:min-h-[100dvh]';
const topButtons = 'grid grid-cols-[1fr_auto_1fr] items-center gap-1 [&>*:first-child]:justify-self-start [&>*:last-child]:justify-self-end';
const cardTitle = 'm-0 text-lg font-semibold text-[var(--text-primary)] text-center';

export const PracticeErrorState = ({ title = 'Practice', error, onBack }) => {
  const navigate = useNavigate();
  const handleBack =
    onBack ||
    (() => {
      const lastOpenedUrl = getLastOpenedUrl();
      navigate(lastOpenedUrl || '/learner/language-learning');
    });

  return (
    <Layout hideSidebar>
      <div className={pageWrapper}>
        <div className={`${cardContainer} practice-state-card`}>
          <div className={topButtons}>
            <PracticeCloseButton onClose={handleBack} />
            <h2 className={cardTitle}>{title}</h2>
            <span />
          </div>
          <div className="py-10 text-center">
            <p className="text-[#d32f2f] mb-5 m-0">Error: {error}</p>
            <button
              onClick={handleBack}
              className="py-2.5 px-5 bg-[#1d4746] text-white border-none rounded-lg cursor-pointer"
            >
              Back to Language Learning
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export const PracticeNoQuestionsState = ({ title = 'Practice', onBack }) => {
  const navigate = useNavigate();
  const handleBack =
    onBack ||
    (() => {
      const lastOpenedUrl = getLastOpenedUrl();
      navigate(lastOpenedUrl || '/learner/language-learning');
    });

  return (
    <Layout hideSidebar>
      <div className={pageWrapper}>
        <div className={`${cardContainer} practice-state-card`}>
          <div className={topButtons}>
            <PracticeCloseButton onClose={handleBack} />
            <h2 className={cardTitle}>{title}</h2>
            <span />
          </div>
          <div className="py-10 text-center">
            <p className="m-0">No practice questions available.</p>
            <button
              onClick={handleBack}
              className="mt-5 py-2.5 px-5 bg-[#1d4746] text-white border-none rounded-lg cursor-pointer"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

